// Constants
import { GetRoleKeys } from "./rbac-roles"
const ROLES_KEYS = GetRoleKeys()

export interface RuleShape {
  static: string[]
  dynamic: {
    [key: string]: (options: any) => boolean
  }
}

export interface RulesShape {
  [key: string]: Partial<RuleShape>
}

export enum RBAC_RULES {
  // ROUTE GUARDING RULES
  VISIT_PERKS_PAGE = "perks-page:visit",
  VISIT_USERS_PAGE = "users-page:visit",
  VISIT_USERS_DETAIL_PAGE = "users-detail-page:visit",
  VISIT_MESSAGES_PAGE = "messages-page:visit",
  VISIT_COMPANIES_PAGE = "companies-page:visit",
  VISIT_DASHBOARD_PAGE = "dashboard-page:visit",
  VISIT_PERK_DETAIL_PAGE = "perk-detail-page:visit",
  VISIT_COMPANY_DETAIL_PAGE = "company-detail-page:visit",
  VISIT_MESSAGE_DETAIL_PAGE = "message-detail-page:visit",
  VISIT_USER_PORTFOLIO_PAGE = "user-portfolio-page:visit",
  VISIT_CREATE_PORTFOLIO_POO_PAGE = "portfolio-poo-page:visit",
  VISIT_POO_HISTORY_PAGE = "poo-history-page:visit",
  VISIT_UPLOAD_FILE_PAGE = "upload-file:visit",
  VISIT_USER_REDEMPTIONS = "user-redemptions-page:visit",
  VISIT_USER_POO = "user-poo-page:visit",
  // METRICS RULES
  SEE_ALL_PERKS_METRICS = "all-perks-metrics:see",
  SEE_ALL_MESSAGES_METRICS = "all-messages-metrics:see",
  SEE_ALL_DASHBOARD_METRICS = "all-dashboard-metrics:see",
  // SEE RULES (visual components)
  SEE_PERK_COMPANY = "perk-company:see",
  SEE_MESSAGE_COMPANY = "message-company:see",
  SEE_PERKS_NAV_LINK = "perks-nav-link:see",
  SEE_USERS_NAV_LINK = "users-nav-link:see",
  SEE_MESSAGES_NAV_LINK = "messages-nav-link:see",
  SEE_COMPANIES_NAV_LINK = "companies-nav-link:see",
  SEE_PORTFOLIO_POO_NAV_LINK = "portfolio-nav-link:see",
  SEE_POO_HISTORY_NAV_LINK = "poo-history-nav-link:see",
  SEE_UPLOAD_FILE_NAV_LINK = "upload-file-nav-link:see",
  // CREATION RULES (POST api calls)
  CREATE_PERK = "perk:post",
  CREATE_COMPANY = "company:post",
  CREATE_MESSAGE = "message:post",
  CREATE_USER_ACCOUNT = "user-account:post",
  CREATE_PERK_REQUEST = "perk-request:post",
  CREATE_MESSAGE_REQUEST = "message-request:post",
  CREATE_FILE = "files:post",
  // VIEW RULES (GET api calls)
  VIEW_PERKS_LIST = "perks-list:view",
  VIEW_PERK_DETAILS = "perk-details:view",
  VIEW_MESSAGES_LIST = "messages-list:view",
  VIEW_MESSAGE_DETAILS = "message-details:view",
  VIEW_USERS_ACCOUNTS_LIST = "users-accounts:view",
  // MODIFICATION RULES (PUT api calls)
  UPDATE_PERK = "perk:update",
  UPDATE_MESSAGE = "message:update",
  UPDATE_USER_ROLES = "user-roles:update",
  UPDATE_PERK_STATUS = "perk-status:update",
  UPDATE_PERK_COMPANY = "perk-company:update",
  UPDATE_USER_COMPANY = "user-company:update",
  UPDATE_USER_ACCOUNT = "user-accounts:update",
  UPDATE_MESSAGE_STATUS = "message-status:update",
  UPDATE_MESSAGE_COMPANY = "message-company:update",
  UPDATE_MESSAGE_PINNED = "message-pinned:update",
  // SUPPORT TICKETS RULES
  VISIT_SUPPORT_TICKETS_PAGE = "support-tickets-page:visit",
  VISIT_SUPPORT_TICKET_DETAIL_PAGE = "support-ticket-detail-page:visit",
  SEE_SUPPORT_TICKETS_NAV_LINK = "support-tickets-nav-link:see",
  VIEW_SUPPORT_TICKETS = "support-tickets:view",
  UPDATE_TICKET_STATUS = "ticket-status:update",
  CREATE_TICKET_MESSAGE = "ticket-message:post",
}

const RULES: RulesShape = {
  [ROLES_KEYS.ROLE_ADMIN]: {
    static: [
      // ROUTE GUARDING
      RBAC_RULES.VISIT_PERKS_PAGE,
      RBAC_RULES.VISIT_USERS_PAGE,
      RBAC_RULES.VISIT_USERS_DETAIL_PAGE,
      RBAC_RULES.VISIT_MESSAGES_PAGE,
      RBAC_RULES.VISIT_COMPANIES_PAGE,
      RBAC_RULES.VISIT_DASHBOARD_PAGE,
      RBAC_RULES.VISIT_PERK_DETAIL_PAGE,
      RBAC_RULES.VISIT_COMPANY_DETAIL_PAGE,
      RBAC_RULES.VISIT_MESSAGE_DETAIL_PAGE,
      RBAC_RULES.VISIT_USER_PORTFOLIO_PAGE,
      RBAC_RULES.VISIT_CREATE_PORTFOLIO_POO_PAGE,
      RBAC_RULES.VISIT_POO_HISTORY_PAGE,
      RBAC_RULES.VISIT_USER_REDEMPTIONS,
      RBAC_RULES.VISIT_USER_POO,
      RBAC_RULES.VISIT_UPLOAD_FILE_PAGE,
      // METRICS
      RBAC_RULES.SEE_ALL_PERKS_METRICS,
      RBAC_RULES.SEE_ALL_MESSAGES_METRICS,
      RBAC_RULES.SEE_ALL_DASHBOARD_METRICS,
      // SEE PERMISSIONS
      RBAC_RULES.SEE_PERK_COMPANY,
      RBAC_RULES.SEE_MESSAGE_COMPANY,
      RBAC_RULES.SEE_PERKS_NAV_LINK,
      RBAC_RULES.SEE_USERS_NAV_LINK,
      RBAC_RULES.SEE_MESSAGES_NAV_LINK,
      RBAC_RULES.SEE_COMPANIES_NAV_LINK,
      RBAC_RULES.SEE_PORTFOLIO_POO_NAV_LINK,
      RBAC_RULES.SEE_POO_HISTORY_NAV_LINK,
      RBAC_RULES.SEE_UPLOAD_FILE_NAV_LINK,
      // VIEW PERMISSIONS
      RBAC_RULES.VIEW_PERKS_LIST,
      RBAC_RULES.VIEW_PERK_DETAILS,
      RBAC_RULES.VIEW_MESSAGE_DETAILS,
      RBAC_RULES.VIEW_USERS_ACCOUNTS_LIST,
      // CREATION PERMISSIONS
      RBAC_RULES.CREATE_PERK,
      RBAC_RULES.CREATE_COMPANY,
      RBAC_RULES.CREATE_MESSAGE,
      RBAC_RULES.CREATE_USER_ACCOUNT,
      // MODIFICATION PERMISSIONS
      RBAC_RULES.UPDATE_PERK,
      RBAC_RULES.UPDATE_MESSAGE,
      RBAC_RULES.UPDATE_USER_ROLES,
      RBAC_RULES.UPDATE_PERK_STATUS,
      RBAC_RULES.UPDATE_PERK_COMPANY,
      RBAC_RULES.UPDATE_USER_COMPANY,
      RBAC_RULES.UPDATE_USER_ACCOUNT,
      RBAC_RULES.UPDATE_MESSAGE_STATUS,
      RBAC_RULES.UPDATE_MESSAGE_COMPANY,
      RBAC_RULES.UPDATE_MESSAGE_PINNED,
      // SUPPORT TICKETS
      RBAC_RULES.VISIT_SUPPORT_TICKETS_PAGE,
      RBAC_RULES.VISIT_SUPPORT_TICKET_DETAIL_PAGE,
      RBAC_RULES.SEE_SUPPORT_TICKETS_NAV_LINK,
      RBAC_RULES.VIEW_SUPPORT_TICKETS,
      RBAC_RULES.UPDATE_TICKET_STATUS,
      RBAC_RULES.CREATE_TICKET_MESSAGE,
    ],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_COMPANY_ADMIN]: {
    static: [
      // CREATION PERMISSIONS
      RBAC_RULES.CREATE_USER_ACCOUNT,
      // MODIFICATION PERMISSIONS
      RBAC_RULES.UPDATE_PERK,
      RBAC_RULES.UPDATE_MESSAGE,
      RBAC_RULES.UPDATE_USER_ROLES,
      // RBAC_RULES.UPDATE_PERK_STATUS,
      // RBAC_RULES.UPDATE_USER_ACCOUNT,
      // RBAC_RULES.UPDATE_MESSAGE_STATUS,
    ],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_COMPANY_USER]: {
    static: [
      // CREATION PERMISSIONS
      RBAC_RULES.CREATE_USER_ACCOUNT,
      // MODIFICATION PERMISSIONS
      RBAC_RULES.UPDATE_PERK,
      RBAC_RULES.UPDATE_MESSAGE,
      RBAC_RULES.UPDATE_USER_ROLES,
      // RBAC_RULES.UPDATE_PERK_STATUS,
      // RBAC_RULES.UPDATE_USER_ACCOUNT,
      // RBAC_RULES.UPDATE_MESSAGE_STATUS,
    ],
  },
  // CCL PERMISSIONS
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_SEABOURN]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_AUS]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_UK]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_HOLLAND_AMERICA_LINE]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_CUNARD]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_CARNIVAL_CRUISE_LINE]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_PRINCESS]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_AIDA]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER_CCL_COSTA]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
  },
  [ROLES_KEYS.ROLE_CUSTOMER_SUPPORT]: {
    static: [
      RBAC_RULES.SEE_PORTFOLIO_POO_NAV_LINK,
      RBAC_RULES.VISIT_CREATE_PORTFOLIO_POO_PAGE,
      RBAC_RULES.SEE_POO_HISTORY_NAV_LINK,
      RBAC_RULES.VISIT_POO_HISTORY_PAGE,
      RBAC_RULES.VISIT_SUPPORT_TICKETS_PAGE,
      RBAC_RULES.VISIT_SUPPORT_TICKET_DETAIL_PAGE,
      RBAC_RULES.SEE_SUPPORT_TICKETS_NAV_LINK,
      RBAC_RULES.VIEW_SUPPORT_TICKETS,
      RBAC_RULES.UPDATE_TICKET_STATUS,
      RBAC_RULES.CREATE_TICKET_MESSAGE,
    ],
  },
  [ROLES_KEYS.ROLE_PERK_CENTRAL_USER]: {
    static: [RBAC_RULES.VISIT_DASHBOARD_PAGE],
    dynamic: {
      [RBAC_RULES.UPDATE_USER_ACCOUNT]: ({ ownerId, userId }) => {
        if (!userId || !ownerId) return false
        return userId === ownerId
      },
    },
  },
}

export default RULES
